/* eslint-disable no-unused-vars */
import { keymap } from 'prosemirror-keymap';
import { history } from 'prosemirror-history';
import {
  baseKeymap, chainCommands, deleteSelection, joinBackward, selectNodeBackward,
} from 'prosemirror-commands';
import { EditorState, Plugin } from 'prosemirror-state';
import { dropCursor } from 'prosemirror-dropcursor';
import { gapCursor } from 'prosemirror-gapcursor';
import {
  columnResizing,
  tableEditing,
} from '@meetshepherd/prosemirror-tables';
import { Node } from 'prosemirror-model';
import buildKeymap from './keymap/index';
import { buildInputRules } from './input-rules/index';
import placeholderPlugin from './images/placeholder-plugin';
import suggestionNavigator from './keymap/suggestion-navigator';
import { todoItemKeymap } from './nodes/checkbox';
import indentation from './keymap/indentation';
import autoLink from './input-rules/auto-link';
import immediateDecoration from './decorations/immediate-decorations';
import linkPreviewDecoration from './plugins/link-creation-preview';
import mentionPromotion from './plugins/mention-promotion';
import tableControlsAdapter from './adapters/table-controls-adapter';
import task from './keymap/task';
import lazyTransaction from './plugins/lazy-transaction';
import { buildKeymap as tableKeymaps } from './computation/handle_tables';

export default function setup(options: Record<string, any>) {
  delete baseKeymap.Enter;
  delete baseKeymap['Mod-a']; // causes curser issues when the placeholder is visible
  const plugins: Plugin<any, any>[] = [
    keymap(tableKeymaps(options.schema, options.mapKeys)),
    buildInputRules(options.schema),
    keymap(task),
    keymap(suggestionNavigator),
    keymap(todoItemKeymap(options.schema)),
    keymap(buildKeymap(options.schema, options.mapKeys, !!options.legacy)),
    keymap(baseKeymap),
    keymap(indentation),
    dropCursor(),
    gapCursor(),
    placeholderPlugin,
    autoLink,
    immediateDecoration,
    linkPreviewDecoration(options.schema),
    mentionPromotion,
    tableEditing({
      allowTableNodeSelection: false,
      callbacks: {
        selectionChangedOnTable: (rects) => {
          if (!rects) {
            tableControlsAdapter.closePreviewControls();
            tableControlsAdapter.closeContextMenu();
          } else {
            tableControlsAdapter.openPreviewControls(rects);
          }
        },
        contextMenuOnCell: (rect) => {
          tableControlsAdapter.openContextMenu();
        },
      },
      cellContentFill: () => {
        const newPara = options.schema.nodes.paragraph.createAndFill({});
        return newPara;
      },
    }),
    columnResizing({}),
    lazyTransaction,
  ];

  if (options.history !== false) {
    plugins.push(history());
  }

  function toggleTodoItemAction(state: EditorState, pos: number, todoItemNode: Node) {
    return state.tr.setNodeMarkup(pos, undefined, { done: !todoItemNode.attrs.done });
  }

  return plugins.concat(new Plugin({
    props: {
      attributes: {
        class: 'ProseMirror-setup-style',
      },
      handleClickOn(editorView, pos, node, nodePos, event) {
        if ((event!.target! as any).classList.contains('todo-checkbox')) {
          editorView.dispatch(toggleTodoItemAction(editorView.state, nodePos, node));
          return true;
        }
        return false;
      },
    },
  }));
}
